import { registerOverlay } from 'klinecharts'
import { type OverlayFigure } from './overlayUtils'
import { CHART_COLORS } from './chartColors'
import {
  buildReferencePriceFigures,
  buildReferencePriceAxisFigures,
  type ReferencePriceFigureInput,
} from './referencePriceLine'
import { NO_DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'

/**
 * Programmatic "dilution level" lines — one full-width dashed horizontal per
 * in-play warrant exercise price (with a matching y-axis price pill), so a
 * trader can see the overhead-supply prices at a glance. Same read-only chrome
 * shape as `referencePriceLine.ts` (locked, id-tracked by the `useDilutionLines`
 * hook, never user-drawn, never persisted), but styled with
 * the accent color rather than the muted grey of the "Today" reference line so
 * the two layers stay visually distinct.
 *
 * Figure construction is DELEGATED to the reference-price builders (single
 * source of truth for the geometry); this module only pins the default color.
 */

export const DILUTION_PRICE_LINE_NAME = 'dilutionPriceLine'

/**
 * Concrete accent color for dilution lines. Canvas overlays can't read CSS
 * vars, so this is a hard value from the shared `CHART_COLORS` palette —
 * `primary` (the only accent token) reads as distinct next to the grey
 * reference line.
 */
export const DILUTION_LINE_COLOR = CHART_COLORS.primary

/** A single dilution price line: an in-play warrant exercise price + a label. */
export interface DilutionLevel {
  price: number
  label?: string
  /** Which family this level belongs to; drives independent gating (showDilutionLevels vs
   *  showOfferingLevels) and the fallback color in `useDilutionLines`. Undefined behaves as
   *  'warrant' for backward compatibility with hosts that don't tag their levels. */
  kind?: 'warrant' | 'offering'
  /** Per-level override for the line/pill color. Falls back to {@link DILUTION_LINE_COLOR}
   *  when omitted (warrant lines have historically relied on that default). */
  color?: string
  /**
   * Epoch ms of the event that created this level (warrant/offering filed_at). When set the
   * line draws from that date instead of the left edge, so the level reads as the dated
   * event it is. Omitted = starts at the left edge (the fallback for rows with no date).
   */
  startTimestamp?: number
  /**
   * Epoch ms of the day the level DIED: its price/volume invalidation date (offerings and
   * warrants alike), or for a warrant its expiration when that comes first. When set the
   * line is a bounded segment ending there; omitted = still active, running to the right edge.
   */
  endTimestamp?: number
}

/** Same geometry as the reference line; color is optional (defaults to the
 *  dilution accent). */
export type DilutionPriceFigureInput = Omit<ReferencePriceFigureInput, 'color'> & {
  color?: string
}

/** Pure main-pane builder: delegates to the reference-line builder, defaulting
 *  the color to the dilution accent. */
export function buildDilutionPriceFigures(input: DilutionPriceFigureInput): OverlayFigure[] {
  return buildReferencePriceFigures({ ...input, color: input.color ?? DILUTION_LINE_COLOR })
}

/** Pure y-axis builder: delegates to the reference-line axis builder with the
 *  dilution accent as the default pill color. */
export function buildDilutionPriceAxisFigures(
  input: DilutionPriceFigureInput & { axisWidth: number },
): OverlayFigure[] {
  return buildReferencePriceAxisFigures({ ...input, color: input.color ?? DILUTION_LINE_COLOR })
}

/** The slice of overlay point/coordinate state the span logic needs. */
interface LevelPoint {
  timestamp?: number
  value?: number
}

/**
 * Which pixel bounds are ANCHORED for a level overlay: a point that carries a
 * timestamp anchors its side of the segment at that date's x (klinecharts maps
 * timestamp to coordinate); a value-only point leaves its side unanchored, so
 * the reference-line builder falls back to the pane edge. Non-finite x (anchor
 * unmappable in the loaded dataset) also reads as unanchored rather than
 * dropping the level.
 */
export function resolveLevelSpan(
  points: LevelPoint[],
  coordinates: Array<{ x: number; y: number }>,
): { startX?: number; endX?: number } {
  const out: { startX?: number; endX?: number } = {}
  if (points[0]?.timestamp != null && Number.isFinite(coordinates[0]?.x)) {
    out.startX = coordinates[0].x
  }
  if (points[1]?.timestamp != null && Number.isFinite(coordinates[1]?.x)) {
    out.endX = coordinates[1].x
  }
  return out
}

/** Register the `dilutionPriceLine` overlay template with klinecharts (once). */
export const registerDilutionPriceLineOverlay = registerOnce(() => {
  registerOverlay({
    name: DILUTION_PRICE_LINE_NAME,
    // Two points (start anchor, end anchor), both supplied programmatically by
    // useDilutionLines; totalStep 3 marks a two-point overlay as complete.
    totalStep: 3,
    lock: true,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: ({
      coordinates,
      overlay,
      bounding,
      precision,
    }: {
      coordinates: Array<{ x: number; y: number }>
      overlay: { points: Array<{ timestamp?: number; value?: number }>; extendData?: unknown }
      bounding: { width: number }
      precision: { price: number }
    }) => {
      const c = coordinates[0]
      const value = overlay.points[0]?.value
      if (!c || value == null) return []
      const ext = (overlay.extendData ?? {}) as { color?: string; label?: string }
      return buildDilutionPriceFigures({
        y: c.y,
        width: bounding.width,
        value,
        pricePrecision: precision.price,
        color: ext.color,
        label: ext.label,
        ...resolveLevelSpan(overlay.points, coordinates),
      })
    },
    createYAxisFigures: ({
      coordinates,
      overlay,
      bounding,
      precision,
    }: {
      coordinates: Array<{ x: number; y: number }>
      overlay: { points: Array<{ value?: number }>; extendData?: unknown }
      bounding: { width: number }
      precision: { price: number }
    }) => {
      const c = coordinates[0]
      const value = overlay.points[0]?.value
      if (!c || value == null) return []
      const ext = (overlay.extendData ?? {}) as { color?: string; ended?: boolean }
      // An ended level (invalidated, or a warrant past expiry) is history, not
      // supply: its segment stays on the chart but the axis label goes.
      if (ext.ended) return []
      return buildDilutionPriceAxisFigures({
        y: c.y,
        width: 0,
        axisWidth: bounding.width,
        value,
        pricePrecision: precision.price,
        color: ext.color,
      })
    },
  } as OverlayTemplateParam)
})
